import React from 'react';
import { Link } from 'react-router-dom';
import { Link2, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { LabSample } from '../../contexts/LabSampleContext';
import { FracasIncident } from '../../types/fracas.types';

interface LabSampleSummaryProps {
    sample: LabSample;
    editedSample: LabSample;
    isEditing: boolean;
    setEditedSample: (sample: LabSample) => void;
    linkedFracas: FracasIncident | null | undefined;
}

export const LabSampleSummary: React.FC<LabSampleSummaryProps> = ({
    sample,
    editedSample,
    isEditing,
    setEditedSample,
    linkedFracas
}) => {
    return (
        <div className="space-y-6">
            {/* Linked Issues */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    Linked Issues
                </h2>
                <div className="space-y-3">
                    {sample.fracasIncidentId && (
                        <Link
                            to={`/fracas/${sample.fracasIncidentId.split('-')[1]}`}
                            className="block p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-blue-900">FRACAS</span>
                                <ExternalLink className="w-4 h-4 text-blue-600" />
                            </div>
                            {linkedFracas && <p className="text-sm text-blue-700 mt-1">{linkedFracas.title}</p>}
                        </Link>
                    )}
                    {sample.eightDReportId && (
                        <Link
                            to={`/8d/${sample.eightDReportId.split('-')[1]}`}
                            className="block p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-purple-900">8D Report</span>
                                <ExternalLink className="w-4 h-4 text-purple-600" />
                            </div>
                            <p className="text-sm text-purple-700 mt-1">{sample.eightDReportId}</p>
                        </Link>
                    )}
                    {!sample.fracasIncidentId && !sample.eightDReportId && (
                        <p className="text-gray-500 text-sm">No linked issues</p>
                    )}
                </div>
            </div>

            {/* Failure Summary */}
            {sample.failureModeConfirmed && (
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        Failure Analysis
                    </h2>
                    <div className="space-y-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Failure Mode</label>
                            <p className="text-gray-900">{sample.failureModeConfirmed}</p>
                        </div>
                        {sample.rootCauseFound && (
                            <div className="flex items-center gap-2 text-sm text-green-700">
                                <CheckCircle className="w-4 h-4" />
                                Root cause confirmed
                            </div>
                        )}
                        {sample.recommendations && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mt-3">Recommendations</label>
                                <p className="text-sm text-gray-600">{sample.recommendations}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Notes</h2>
                {isEditing ? (
                    <textarea
                        value={editedSample.notes || ''}
                        onChange={e => setEditedSample({ ...editedSample, notes: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Add notes..."
                    />
                ) : (
                    <p className="text-gray-700 whitespace-pre-wrap">{sample.notes || 'No notes added.'}</p>
                )}
            </div>
        </div>
    );
};
